import type { GoogleLoginIntent } from '../../server/auth/contracts.js';
import { captureFrontendException } from '../observability/sentry.js';

export type GisButtonCleanup = () => void;

export interface GisButtonRenderer {
  render(container: HTMLElement, intent: GoogleLoginIntent): Promise<GisButtonCleanup | void>;
}

interface GoogleIdentityApi {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        nonce: string;
        ux_mode: 'popup';
        auto_select: false;
        callback(response: { credential: string }): void;
      } | {
        client_id: string;
        nonce: string;
        ux_mode: 'redirect';
        login_uri: string;
        auto_select: false;
      }): void;
      renderButton(container: HTMLElement, config: {
        type: 'standard';
        theme: 'outline';
        size: 'large';
        text: 'signin_with';
        shape: 'rectangular';
        locale: 'ru' | 'en';
        state?: string;
      }): void;
    };
  };
}

export interface OfficialGisButtonRendererOptions {
  fetch?: typeof fetch;
  navigate?: (path: string) => void;
  uxMode?: 'popup' | 'redirect';
}

const GIS_SCRIPT_BASE = 'https://accounts.google.com/gsi/client';
let scriptPromise: Promise<void> | undefined;

export function googleButtonLocale(browserLanguage: string | undefined): 'ru' | 'en' {
  return browserLanguage?.trim().toLowerCase().split(/[-_]/u, 1)[0] === 'ru' ? 'ru' : 'en';
}

/** Google requires redirect mode on iOS because all iOS browsers inherit ITP. */
export function googleButtonUxMode(
  userAgent: string | undefined,
  platform: string | undefined,
  maxTouchPoints: number | undefined,
): 'popup' | 'redirect' {
  if (/\b(iPad|iPhone|iPod)\b/u.test(userAgent ?? '')) return 'redirect';
  // iPadOS may request a desktop UA and identify itself as MacIntel.
  if (platform === 'MacIntel' && (maxTouchPoints ?? 0) > 1) return 'redirect';
  return 'popup';
}

function googleIdentity(): GoogleIdentityApi | undefined {
  return (globalThis as typeof globalThis & { google?: GoogleIdentityApi }).google;
}

function loadGoogleIdentityScript(documentRef: Document, locale: 'ru' | 'en'): Promise<void> {
  if (googleIdentity() !== undefined) return Promise.resolve();
  if (scriptPromise !== undefined) return scriptPromise;
  const scriptSource = `${GIS_SCRIPT_BASE}?hl=${locale}`;
  const pending = new Promise<void>((resolve, reject) => {
    const existing = documentRef.querySelector<HTMLScriptElement>(`script[src="${scriptSource}"]`);
    const script = existing ?? documentRef.createElement('script');
    const owned = existing === null;
    const loaded = (): void => {
      if (googleIdentity() === undefined) {
        script.remove();
        reject(new Error('Google Identity Services не инициализирован.'));
        return;
      }
      resolve();
    };
    script.addEventListener('load', loaded, { once: true });
    script.addEventListener('error', () => {
      script.remove();
      reject(new Error('Не удалось загрузить Google Identity Services.'));
    }, { once: true });
    if (owned) {
      script.src = scriptSource;
      script.async = true;
      documentRef.head.append(script);
    }
  });
  scriptPromise = pending.catch((reason: unknown) => {
    scriptPromise = undefined;
    throw reason;
  });
  return scriptPromise;
}

/** Official GIS popup button. Calling `prompt` (One Tap) is intentionally absent. */
export function createOfficialGisButtonRenderer(
  documentRef: Document = document,
  browserLanguage: string | undefined = documentRef.defaultView?.navigator.language,
  options: OfficialGisButtonRendererOptions = {},
): GisButtonRenderer {
  const browserFetch = options.fetch
    ?? documentRef.defaultView?.fetch.bind(documentRef.defaultView)
    ?? globalThis.fetch.bind(globalThis);
  const navigate = options.navigate ?? ((path: string) => {
    documentRef.defaultView?.location.assign(path);
  });
  return {
    async render(container, intent) {
      const locale = googleButtonLocale(browserLanguage);
      const navigatorRef = documentRef.defaultView?.navigator;
      const uxMode = options.uxMode ?? googleButtonUxMode(
        navigatorRef?.userAgent,
        navigatorRef?.platform,
        navigatorRef?.maxTouchPoints,
      );
      await loadGoogleIdentityScript(documentRef, locale);
      const google = googleIdentity();
      if (google === undefined) throw new Error('Google Identity Services не инициализирован.');
      let active = true;
      if (uxMode === 'redirect') {
        google.accounts.id.initialize({
          client_id: intent.clientId,
          nonce: intent.nonce,
          ux_mode: 'redirect',
          login_uri: intent.loginUri,
          auto_select: false,
        });
      } else {
        google.accounts.id.initialize({
          client_id: intent.clientId,
          nonce: intent.nonce,
          ux_mode: 'popup',
          auto_select: false,
          callback: ({ credential }) => {
            void (async () => {
              try {
                const response = await browserFetch(intent.loginUri, {
                  method: 'POST',
                  credentials: 'same-origin',
                  cache: 'no-store',
                  headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Cards-Login-Callback': '1',
                  },
                  body: JSON.stringify({ credential, state: intent.state }),
                });
                const payload = await response.json() as unknown;
                if (!response.ok
                  || typeof payload !== 'object'
                  || payload === null
                  || Array.isArray(payload)
                  || Object.keys(payload).length !== 1
                  || typeof (payload as { redirectTo?: unknown }).redirectTo !== 'string'
                  || !(payload as { redirectTo: string }).redirectTo.startsWith('/')
                  || (payload as { redirectTo: string }).redirectTo.startsWith('//')) {
                  throw new Error('Google callback rejected');
                }
                if (active) navigate((payload as { redirectTo: string }).redirectTo);
              } catch (reason) {
                captureFrontendException(reason, { scope: 'google_popup_callback' });
                if (active) {
                  container.replaceChildren(documentRef.createTextNode(
                    locale === 'ru'
                      ? 'Не удалось войти. Обновите страницу и попробуйте снова.'
                      : 'Sign-in failed. Reload the page and try again.',
                  ));
                }
              }
            })();
          },
        });
      }
      google.accounts.id.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        locale,
        ...(uxMode === 'redirect' ? { state: intent.state } : {}),
      });
      return () => {
        active = false;
        container.replaceChildren();
      };
    },
  };
}
